import { extInfo } from 'lib/extInfo';
import { IS_ONPREMISE } from 'reactApp/appHelpers/configHelpers';
import { getFeatureShareMountEditAccessDisable, getFeatureWopiCoEdit } from 'reactApp/modules/features/features.selectors';
import { isMountedOrSharedFolder } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { RootState } from 'reactApp/store';
import { createSelector } from 'reselect';
import { EEditors } from 'server/helpers/editors/getEditorsConfig';

import { getAvailableEditorsForItem, isValidEditableItem } from './editor.helpers';
import { CreatorsDescRecord, EditorState, Extension } from './editor.types';

export const getEditorState = (state: RootState): EditorState => state.editor;

const CO_EDITORS = [EEditors.MYOFFICE, EEditors.R7];

export const getEditorItemById = createSelector(
    getEditorState,
    (_: RootState, id: string) => id,
    (state, id) => {
        if (state.item && state.item.id === id) {
            return state.item;
        }
    }
);

const getDefaultCoEditorId = createSelector(
    (state: RootState) => state,
    (_, item: any) => item,
    (state: RootState, item: any) => isMountedOrSharedFolder(state, item),
    getEditorState,
    (_, item, folderState, editor): string | undefined | null => {
        if (!item) {
            return null;
        }
        const { sharedExtensions, extensions, availableEditors } = editor;
        const hasSharedEditor = editor.hasSharedEditor && sharedExtensions[item.ext];
        const isSharedFolder = hasSharedEditor && folderState.is;

        const editors = extensions[(IS_ONPREMISE && item.ext ? item.ext.toLowerCase() : item.ext) as Extension]?.editors || [];

        return editors.find((editorId) => {
            if (!CO_EDITORS.includes(editorId as EEditors)) {
                return false;
            }
            return hasSharedEditor && isSharedFolder
                ? availableEditors[editorId].default !== 'shared-folder'
                : availableEditors[editorId].default || editors.length === 1;
        });
    }
);

export const getIsFileForCoEditing = createSelector(
    (state: RootState) => state,
    (_: RootState, item: any) => item,
    (state: RootState, item: any) => getDefaultCoEditorId(state, item),
    (state) => getFeatureWopiCoEdit(state),
    (state) => getFeatureShareMountEditAccessDisable(state),
    (_, item, defaultEditor, wopiCoEdit, isShareMountEditAccessDisable) => {
        if (!item || item.isFolder) {
            return false;
        }

        switch (defaultEditor) {
            case EEditors.R7:
                return true;
            case EEditors.MYOFFICE: {
                if (!wopiCoEdit) {
                    return false;
                }

                // запрещаем шарить на редактирование документ, если мы не владельцы
                return isShareMountEditAccessDisable ? !item.isMounted : true;
            }
        }
    }
);

export const getCreateDescriptions = createSelector(getEditorState, ({ extensions }) =>
    Object.values(extensions).reduce((acc, ext) => (ext.creatorsDesc ? acc.concat(ext.creatorsDesc) : acc), [] as CreatorsDescRecord[])
);
export const getEditorItem = createSelector(getEditorState, (state) => state.item);

export const getEditorsFor = createSelector(
    (state: RootState) => state,
    (_: RootState, item: any) => item,
    (state: RootState, item) => isMountedOrSharedFolder(state, item),
    (state: RootState) => state.editor,
    (_, item, folderState, editor) => {
        if (!isValidEditableItem(item)) {
            return [];
        }

        const result = getAvailableEditorsForItem(item, editor, folderState);

        const { ext, size } = item;
        const extData = extInfo.get(ext);

        return result.filter((item) => {
            const maxSize =
                typeof extData.maxEditableSize === 'object'
                    ? extData.maxEditableSize[item.id] || extData.maxEditableSize['default']
                    : extData.maxEditableSize;

            return maxSize && size <= maxSize;
        });
    }
);

export const getAvailableConfiguredEditorsFor = createSelector(
    (state: RootState) => state,
    (_: RootState, item: any) => item,
    (state: RootState, item) => isMountedOrSharedFolder(state, item),
    (state: RootState) => state.editor,
    (_, item, folderState, editor) => {
        return getAvailableEditorsForItem(item, editor, folderState);
    }
);

export const getEditorsByExtension = createSelector(
    getEditorState,
    (_: RootState, ext: Extension) => ext,
    ({ extensions }, ext) => (extensions[ext] || { editors: [] }).editors
);

/**
 * Проверяет доступность переименования файла
 * **Важно:** Пока доступно только для WOPI-версии Моего Офиса
 */
export const isEnableRenamingInsideEditor = createSelector(
    (state: RootState) => ({ state, editor: getEditorState(state) }),
    ({ state, editor: { editorId, item } }) => {
        if (!editorId || !item) {
            return false;
        }

        if (editorId === EEditors.MYOFFICE) {
            return ViewerSelectors.isWopiEnabled(state, item.ext.toLowerCase());
        }

        return false;
    }
);

export const isEditorStorage = (storage): boolean =>
    [EStorageType.editor, EStorageType.office, EStorageType.r7, EStorageType.myoffice, EStorageType.myofficeAttaches].includes(storage);
